

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

import {
  mountSessionInAudioEngine,
  syncAudioEngineChannelSettings,
  unmountSessionInAudioEngine,
  destructProcessorMixerEngine,
  packSession,
  unpackSession,
  startAudioEngine,
  renderSession,
  resetAudioEngineChannelSettings
} from "./sessionAPI";


import audioEngine from "../../services/audioEngine";

import { removeAssetFromCache } from '../../services/assetStore';

import initialState from "./initialState";
import { triggerBlobDownload } from "../../factories/ui";

import { saveLibrarySongSettings } from "../songSettings/songSettingsSlice";
// and if needed:
import { updateSetlistItemSettings } from "../setlists/setlistsSlice";


// pgw 09-feb-2026
let doprint = false;
// let doprint = true;



export const specialCaseChannels = (clientConfig) => clientConfig.features.session.introChannelNames.concat(
  clientConfig.features.session.click1ChannelNames
)
  .concat(clientConfig.features.session.click2ChannelNames)
  .concat(clientConfig.features.session.cueChannelNames);


const mergeMixerSettings = (targetSession, sourceSession) => {
  if (!targetSession || !sourceSession) return targetSession;

  const isNonEmptyArray = (a) => Array.isArray(a) && a.length > 0;

  const pick = (src, key, fallback) =>
    src && src[key] !== undefined ? src[key] : fallback;

  const mergeChannels = (target = [], source = []) => {
    // ✅ If source is empty, do NOT touch target (prevents clobbering)
    if (!isNonEmptyArray(source) || !isNonEmptyArray(target)) return target;

    // Prefer index matching when available
    const sourceByIndex = new Map(
      source
        .filter(s => s && s.index !== undefined && s.index !== null)
        .map(s => [s.index, s])
    );

    const sourceById = new Map(
      source
        .filter(s => s && s.id !== undefined && s.id !== null)
        .map(s => [s.id, s])
    );

    return target.map(t => {
      if (!t) return t;

      const s =
        (t.index !== undefined && t.index !== null && sourceByIndex.get(t.index)) ||
        (t.id !== undefined && t.id !== null && sourceById.get(t.id));

      if (!s) return t;

      return {
        ...t,
        level: pick(s, "level", t.level),
        muted: pick(s, "muted", t.muted),
        soloed: pick(s, "soloed", t.soloed),
        pan: pick(s, "pan", t.pan),
        armed: pick(s, "armed", t.armed),
      };
    });
  };

  targetSession.stems = mergeChannels(targetSession.stems, sourceSession.stems);
  targetSession.userStems = mergeChannels(targetSession.userStems, sourceSession.userStems);

  // -------------------------
  // Globals (preserve shape)
  // -------------------------

  // speed/pitch in your session state are objects: { value, displayValue }
  // Only copy if source has a real value (and keep target object if present)
  if (sourceSession.speed !== undefined && sourceSession.speed !== null) {
    if (typeof sourceSession.speed === "object") {
      targetSession.speed = {
        ...(targetSession.speed || {}),
        ...sourceSession.speed,
      };
    } else {
      // source is a number
      targetSession.speed = {
        ...(targetSession.speed || {}),
        value: sourceSession.speed,
      };
    }
  }

  if (sourceSession.pitch !== undefined && sourceSession.pitch !== null) {
    if (typeof sourceSession.pitch === "object") {
      targetSession.pitch = {
        ...(targetSession.pitch || {}),
        ...sourceSession.pitch,
      };
    } else {
      targetSession.pitch = {
        ...(targetSession.pitch || {}),
        value: sourceSession.pitch,
      };
    }
  }

  if (typeof sourceSession.masterMute === "boolean") {
    targetSession.masterMute = sourceSession.masterMute;
  }

  return targetSession;
};





export const mountSessionAsync = createAsyncThunk(
  "session/mount",
  async (payload, thunkAPI) => {
    thunkAPI.dispatch(stopSong());
    const loadedSession = thunkAPI.getState().session;
    const clientConfig = thunkAPI.getState().ui.clientConfig;

    thunkAPI.dispatch(updateStatus('loading'));

	try {
// 		console.log('mountSessionAsync p1 audioEngine.audioReady = ', audioEngine.audioReady);
// 		console.log('mountSessionAsync p1 loadedSession.song.numStems = ', loadedSession.song.numStems);
	}
	catch(e) {
		console.log('mountSessionAsync e = ', e);
	}

    if (!audioEngine.audioReady) {
      await startAudioEngine();
      audioEngine.webaudioManager.audioContext.resume();
    } else {


	  // pgw 24-feb-2026 - Testing fix for no audio when opening songs from setlist
	  // IMPORTANT:
	  // Don't destruct here. mountSessionInAudioEngine() will resize/rebuild the mixer engine
	  // using the *actual* new stem count (encodedAudioBuffers.length).
	  // Also, loadedSession.song.numStems could be zero, which can screw up audio
      // await destructProcessorMixerEngine(loadedSession.song.numStems);

    }

    if (doprint) console.log("[SYNC] using stems 1 ", loadedSession?.stems?.map(s => ({
      id: s.id, index: s.index, level: s.level, muted: s.muted
    })));

    const updatedSession = await mountSessionInAudioEngine(
      loadedSession,
      false,
      (progressObject) => thunkAPI.dispatch(updateProgress(progressObject)),
      clientConfig,
      payload.cache
    );


// ✅ CRITICAL: preserve origin + identity across engine mount
updatedSession.origin = loadedSession.origin || updatedSession.origin || null;
updatedSession.id = loadedSession.id || updatedSession.id;
updatedSession.title = loadedSession.title || updatedSession.title;

// if you rely on songId separately anywhere
updatedSession.songId = loadedSession.songId || updatedSession.songId;


if (doprint) console.log("[MOUNT] preserved origin after engine mount", {
  before: loadedSession?.origin,
  after: updatedSession?.origin,
});

    if (doprint) console.log("[SYNC] using stems 1a ", updatedSession?.stems?.map(s => ({
      id: s.id, index: s.index, level: s.level, muted: s.muted
    })));

    // ============================================================
    // ✅ APPLY RESTORE SETTINGS NOW (this is the correct moment)
    // ============================================================
    const restoreSettings = loadedSession?.restoreSettings || null; // <-- adjust if stored elsewhere
    const restoreStems = restoreSettings?.stems || [];

    if (restoreStems.length && (updatedSession?.stems?.length || 0) > 0) {
      const restoreByIndex = new Map(restoreStems.map(r => [r.index, r]));

      const before = updatedSession.stems.map(s => ({ index: s.index, level: s.level, muted: s.muted, soloed: s.soloed, pan: s.pan }));

      updatedSession.stems = updatedSession.stems.map(stem => {
        const r = restoreByIndex.get(stem.index);
        if (!r) return stem;
        return {
          ...stem,
          level: r.level ?? stem.level,
          muted: r.muted ?? stem.muted,
          soloed: r.soloed ?? stem.soloed,
          pan: r.pan ?? stem.pan,
        };
      });

      const after = updatedSession.stems.map(s => ({ index: s.index, level: s.level, muted: s.muted, soloed: s.soloed, pan: s.pan }));

      if (doprint) console.log("[MOUNT] applied restoreSettings to mounted stems", {
        restoreCount: restoreStems.length,
        before,
        after,
      });
    } else {
      if (doprint) console.log("[MOUNT] no restore applied", {
        hasRestoreSettings: !!restoreSettings,
        restoreCount: restoreStems.length,
        mountedStemCount: updatedSession?.stems?.length || 0,
      });
    }

    // 🔥 keep restored mixer from redux, not engine defaults
    // (If mergeMixerSettings currently overwrites updatedSession with loadedSession defaults,
    // you may want it to NOT undo the restore we just applied.)
    mergeMixerSettings(updatedSession, loadedSession);

    await syncAudioEngineChannelSettings(updatedSession);

    if (doprint) console.log("[ENGINE SYNC] called with updatedSession ", {
      id: updatedSession?.id,
      status: updatedSession?.status,
      stems: updatedSession?.stems?.map(s => ({ id: s.id, index: s.index, level: s.level })),
    });

    return updatedSession;
  }
);


export const mountSessionAndRenderOfflineAsync = createAsyncThunk(
  "session/mountAndRender",
  async (payload, thunkAPI) => {
    thunkAPI.dispatch(stopSong());
    const loadedSession = thunkAPI.getState().session;
    const clientConfig = thunkAPI.getState().ui.clientConfig;
    // await destructProcessorMixerEngine(loadedSession.song.len);

    thunkAPI.dispatch(updateStatus('loading'));

    await destructProcessorMixerEngine(loadedSession.song.tracks.length);
    // change the length of the render based on session speed
    await startAudioEngine({ length: loadedSession.song.len / Math.max(.5, Math.min(1.5, Number(loadedSession.speed?.value) || 1)) }); 
    if (audioEngine.processorNode) audioEngine.processorNode.disconnect();
    audioEngine.processorNode = null;

    thunkAPI.dispatch(updateProgress({step:7, downloadPercentage:100}));
    const updatedSession = await mountSessionInAudioEngine(
      loadedSession,
      true,
      (value) => thunkAPI.dispatch(updateProgress(value)),
      clientConfig,
      payload.cache
    );

	if (doprint) console.log("[SYNC] using stems 2 loadedSession ", loadedSession?.stems?.map(s => ({ id: s.id, level: s.level, muted: s.muted })));
	if (doprint) console.log("[SYNC] using stems 2 updatedSession ", updatedSession?.stems?.map(s => ({ id: s.id, level: s.level, muted: s.muted })));

    await syncAudioEngineChannelSettings(updatedSession);

	if (doprint) console.log("[ENGINE SYNC] called with", {
	  id: updatedSession?.id,
	  status: updatedSession?.status,
	  stems: updatedSession?.stems?.map(s => ({ id: s.id, index: s.index, level: s.level })),
	});


    thunkAPI.dispatch(updateProgress({step:8, downloadPercentage:100}));
    const dateNow = new Date();
    await renderSession(`mix_${dateNow.toLocaleDateString().replaceAll('/','')}_${dateNow.toLocaleTimeString().replaceAll(':','')}`);
    thunkAPI.dispatch(updateProgress({step:9, downloadPercentage:100}));

    //remount the online audiocontext session
    // await thunkAPI.dispatch(unmountSessionAsync());
    // await thunkAPI.dispatch(openSession(loadedSession));
    // await thunkAPI.dispatch(mountSessionAsync());
    await thunkAPI.dispatch(updateStatus('loading'));
    return updatedSession;
  }
);


const buildPersistedSettingsFromSession = (session) => ({
  stems: (session.stems || []).map(s => ({
    index: s.index,
    level: s.level,
    muted: !!s.muted,
    soloed: !!s.soloed,
    pan: s.pan,
  })),
  speed: session.speed?.value ?? session.speed,
  pitch: session.pitch?.value ?? session.pitch,
  masterMute: !!session.masterMute,
  updatedAt: new Date().toISOString(),
});



export const resetSessionMixerSettings = createAsyncThunk(
  "session/reset",
  async (_payload, thunkAPI) => {
    const clientConfig = thunkAPI.getState().ui.clientConfig;

    // 1) Reset Redux first
    thunkAPI.dispatch(resetMixerState());

    // 2) Read the post-reset session
    const resetSession = thunkAPI.getState().session;

    if (doprint) console.log("[RESET] redux after resetMixerState", {
      speed: resetSession.speed,
      pitch: resetSession.pitch,
      stems: resetSession.stems?.map(s => ({ index: s.index, level: s.level })),
    });

    if (!resetSession?.id || !resetSession?.stems?.length) return;

    // 3) Reset engine (your existing call)
    await resetAudioEngineChannelSettings(resetSession, clientConfig);

    // 4) Persist the reset defaults IMMEDIATELY (this is the missing part)
    const origin = resetSession.origin;
    const settings = buildPersistedSettingsFromSession(resetSession);

    if (origin?.type === "setlist") {



      thunkAPI.dispatch(updateSetlistItemSettings({
        setlistId: origin.setlistId,
        itemIx: origin.itemIx,
        itemSettings: settings,
      }));
    } else {
      const songId = origin?.songId || resetSession.song?.songId;
      if (songId) {
        thunkAPI.dispatch(saveLibrarySongSettings({ songId, settings }));
      }
    }

    return true;
  }
);

export const unmountSessionAsync = createAsyncThunk(
  "session/unmount",
  async (payload, thunkAPI) => {
    
    // await unmountSessionInAudioEngine();
    
    return initialState;
  }
);

export const packSessionAsync = createAsyncThunk(
  "session/pack",
  async (payload, thunkAPI) => {
    const loadedSession = thunkAPI.getState().session;
    const sessionBlob = await packSession(loadedSession);
    triggerBlobDownload(sessionBlob);
    return true;
  }
);



const findChannel = (state, index, userStem) => {
  return state[userStem ? "userStems" : "stems"].find((s) => s.index === index);
};

export const attachUserMicrophoneAsync = createAsyncThunk(
  "session/attachUserInput",
  async (thunkAPI) => {
    return await audioEngine.requestUserMicrophone();
  }
);

export const removeChannelAsync = createAsyncThunk(
  "session/removeChannel",
  async (payload, thunkAPI) => {
    await audioEngine.removeChannel(
      payload.index
    );
    await removeAssetFromCache(payload.id);
    return payload;
  }
);

//todo - the whole state[action.payload.userStem ? "userStems" : "stems"] thing is really messy, replace with one function.

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {

  // pgw 01-may-2026 - testing edit lyrics and chapters
  updateSessionMetadata: (state, action) => {
	const { chapters, lyrics, songPatch } = action.payload || {};

	if (!state.song) return;

	if (songPatch && typeof songPatch === "object") {
	  state.song = {
		...state.song,
		...songPatch,
	  };

	  if (songPatch.mix || songPatch.title) {
		state.title = songPatch.mix || songPatch.title;
	  }

	  if (songPatch.len || songPatch.duration) {
		state.duration = Number(songPatch.len || songPatch.duration || 0);
	  }

	  if (Array.isArray(songPatch.stems) && songPatch.stems.length) {
		state.stems = state.stems.map((stem, index) => {
		  const updatedStem = songPatch.stems[index];
		  const updatedTrackName = songPatch.tracknames?.[index];

		  const newLabel =
			updatedStem?.label ||
			updatedStem?.id ||
			updatedTrackName?.name ||
			stem.label;

		  return {
			...stem,
			id: newLabel,
			label: newLabel,
		  };
		});
	  }
	}



	if (Array.isArray(chapters)) {
	  state.song.chapters = chapters;

	  state.sections = chapters.length
		? chapters.map((chapter, index) => ({
			label: chapter.name || `Section ${index + 1}`,
			start: Number(chapter.time) || 0,
			end: Number(chapters[index + 1]?.time) || Number(state.song?.len || 0),
		  }))
		: [
			{
			  label: "Start",
			  start: 0,
			  end: Number(state.song?.len || 0),
			},
		  ];
	}

	if (Array.isArray(lyrics)) {
	  state.song.lyricsOverride = lyrics;
	}


  },


  resetMixerState: (state) => {
	const resetChannels = (arr) => {
	  for (const ch of arr || []) {
		ch.level = 1;
		ch.muted = false;
		ch.soloed = false;
		ch.pan = 0.5;
		if (ch.isInputChannel) ch.armed = false;
	  }
	};

	resetChannels(state.stems);
	resetChannels(state.userStems);

	// globals
	if (state.speed && typeof state.speed === "object") {
	  state.speed.value = 1;
	  state.speed.displayValue = "100%";
	} else {
	  state.speed = { value: 1, displayValue: "100%" };
	}

	if (state.pitch && typeof state.pitch === "object") {
	  state.pitch.value = 0;
	  state.pitch.displayValue = "0";
	} else {
	  state.pitch = { value: 0, displayValue: "0" };
	}

	state.masterMute = false;
  },


    updateProgress: (state, action) => {
      state.loadingProgress = action.payload;
    },
    openSession: (state, action) => {
		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE openSession - state = ", state);
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE openSession - action = ", action);

		if (doprint) console.log("[OPENSESSION] input stems", action.payload?.stems?.map(s => ({id:s.id, level:s.level})));

		if (!action.payload?.stems?.length) {
// 		  console.warn("[OPENSESSION] payload has no stems!", action.payload);
		}

      Object.getOwnPropertyNames(action.payload).forEach((propertyName) => {
        state[propertyName] = action.payload[propertyName];
      });
      state.status = "unmounted";
    },
    resetLoadedStatus: (state) => {
      // state.status = "empty";
      // state.loadingProgress = 0;
      // state.loadedSong = null;
    },
    changeGlobalPlaybackRate: (state, action) => {
      if (state.playbackState === "recording") return;
		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeGlobalPlaybackRatev - state = ", state);
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeGlobalPlaybackRate - action = ", action);
      const denormalised = (action.payload + 1.5) / 2.0;
      state.speed.displayValue = String((denormalised * 100).toFixed(0) + "%");
      state.speed.value = denormalised;
      audioEngine.sendGlobalParamChange("playbackRate", denormalised).catch(() => {});
    },
    decreaseGlobalPlaybackRate: (state) => {
      if (state.playbackState === "recording") return;
		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE decreaseGlobalPlaybackRate - state = ", state);
      if (state.speed.value > 0.5) {
        state.speed.value = Number((state.speed.value - 0.1).toFixed(1));
        state.speed.displayValue = `${(state.speed.value * 100).toFixed()}%`;
        audioEngine.sendGlobalParamChange("playbackRate", state.speed.value).catch(() => {});
      }
    },
    increaseGlobalPlaybackRate: (state) => {
      if (state.playbackState === "recording") return;
		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE increaseGlobalPlaybackRate - state = ", state);
      if (state.speed.value < 1.5) {
        state.speed.value = Number((state.speed.value + 0.1).toFixed(1));
        state.speed.displayValue = `${(state.speed.value * 100).toFixed()}%`;
        audioEngine.sendGlobalParamChange("playbackRate", state.speed.value).catch(() => {});
      }
    },
    changeGlobalPitch: (state, action) => {
      if (state.playbackState === "recording") return;

		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeGlobalPitch - state = ", state);
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeGlobalPitch - action = ", action);
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeGlobalPitch - action.payload = ", action.payload);
      const scale = action.payload / 1;
      const denormalised = (500 - -500) * scale + -500;
      state.pitch.displayValue = String(denormalised.toFixed(0));
      state.pitch.value = denormalised;
      audioEngine.sendGlobalParamChange("pitchShiftCents", denormalised).catch(() => {});
    },
    decreaseGlobalPitchRate: (state) => {
      if (state.playbackState === "recording") return;

		// pgw 26-jan-2026
		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE decreaseGlobalPitchRate - state = ", state);

      // pgw 25-jun-2026 testing change 500 to 900
      // pgw 27-jun-2026 testing change 900 to 1200
      if (state.pitch.value > -1200) {
        state.pitch.value = state.pitch.value - 100;
        state.pitch.displayValue = `${state.pitch.value>0?'+':''}${state.pitch.value/100}${state.pitch.value !== 0 ? 'st':''}`;
        audioEngine.sendGlobalParamChange("pitchShiftCents", state.pitch.value).catch(() => {});
      }
      
    },
    increaseGlobalPitchRate: (state) => {
      if (state.playbackState === "recording") return;

		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE increaseGlobalPitchRate - state = ", state);

      // pgw 25-jun-2026 testing change 500 to 900
      // pgw 27-jun-2026 testing change 900 to 1200
      if (state.pitch.value < 1200) {
        state.pitch.value = state.pitch.value + 100;
        state.pitch.displayValue = `${state.pitch.value>0?'+':''}${state.pitch.value/100}${state.pitch.value !== 0 ?'st':''}`;
        audioEngine.sendGlobalParamChange("pitchShiftCents", state.pitch.value).catch(() => {});
      }
    },
    changeChannelGain: (state, action) => {

		// pgw 26-jan-2026
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeChannelGain - state = ", state);
		if (doprint) console.log("[FEATURES_SESSION_SESSIONSLICE changeChannelGain - action = ", action);


		// pgw 07-dec-2025
	  if (doprint) console.log("v6 [MIXER] changeChannelGain BEFORE", {
		index: action.payload.index,
		value: action.payload.value,
		stems: state.stems.map(s => ({ id: s.id, level: s.level })),
	  });

      findChannel(state);
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).level = action.payload.value;



		// pgw 07-dec-2025
	  if (doprint) console.log("v6 [MIXER] changeChannelGain AFTER", {
		stems: state.stems.map(s => ({ id: s.id, level: s.level })),
	  });


      audioEngine.sendChannelParamChange(
        action.payload.index,
        "channelGain",
        action.payload.value
      );
    },
    muteChannel: (state, action) => {


		// pgw 07-dec-2025
	/*
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).muted = true;
	*/

      const target = state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index);


	  if (doprint) console.log("v6 [MIXER] muteChannel BEFORE", {
		id: target.id,
		muted: target.muted
	  });


	  target.muted = true;

	  if (doprint) console.log("v6 [MIXER] muteChannel AFTER", {
		id: target.id,
		muted: target.muted
	  });


      audioEngine.sendChannelParamChange(action.payload.index, "mute", true);
    },
    unmuteChannel: (state, action) => {

      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).muted = false;
      audioEngine.sendChannelParamChange(action.payload.index, "mute", false);
    },
    soloChannel: (state, action) => {

      if (
        state[action.payload.userStem ? "userStems" : "stems"].find(
          (s) => s.index === action.payload.index
        ).muted
      )
        state[action.payload.userStem ? "userStems" : "stems"].find(
          (s) => s.index === action.payload.index
        ).muted = false;
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).soloed = true;
      audioEngine.sendChannelParamChange(action.payload.index, "mute", false);
      for (
        let index = 0; index < state.stems.concat(state.userStems).length; index++
      ) {
        // console.log(index,state.stems.concat(state.userStems) );
        const stem = state.stems.concat(state.userStems)[index];

        if (!stem.soloed) audioEngine.sendChannelParamChange(stem.index, "mute", true);
      }
    },
    armChannel: (state, action) => {
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).muted = true;
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).armed = true;
      audioEngine.sendChannelParamChange(action.payload.index, "arm", true);
      audioEngine.sendChannelParamChange(action.payload.index, "mute", true);
    },
    disarmChannel: (state, action) => {
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).armed = false;
      audioEngine.sendChannelParamChange(action.payload.index, "arm", false);
    },
    unsoloChannel: (state, action) => {
      state[action.payload.userStem ? "userStems" : "stems"].find(
        (s) => s.index === action.payload.index
      ).soloed = false;

      audioEngine.sendChannelParamChange(action.payload.index, "mute", true);
      if (
        !state[action.payload.userStem ? "userStems" : "stems"].find(
          (s) => s.soloed
        )
      ) {
        for (let index = 0; index < state.stems.concat(state.userStems).length; index++) {
          const stem =
            state.stems.concat(state.userStems)[index];
          if (!stem.muted)
            audioEngine.sendChannelParamChange(index, "mute", false);
        }
      }
    },
    changeChannelPan: (state, action) => {
      state[action.payload.userStem ? "userStems" : "stems"].find(s=>s.index === action.payload.index).pan = action.payload.value;
      audioEngine.sendChannelParamChange(
        action.payload.index,
        "pan",
        action.payload.value
      );
    },


	playSong: (state) => {
	  if (doprint) console.log("pgw playSong state = ", state)

	  state.playbackState = "playing";
	  audioEngine.sendPlayCommand();
	},


    recordSong: (state) => {
      state.speed = { value: 1, displayValue: "100%" };
      state.pitch = { value: 0, displayValue: "0" };
      audioEngine.sendLoopCommand(null, null);
      state.isLooping = false;
      state.playbackState = "recording";
      audioEngine.sendRecordCommand();
    },
    pauseSong: (state) => {
		if (doprint) console.log("pgw pauseSong state = ", state)
      state.playbackState = "paused";
      audioEngine.sendPauseCommand();
      

      //show feedback modal if not shown before

    },


	seek: (state, action) => {

	  if (doprint) console.log("pgw seek state = ", state)
	  if (doprint) console.log("pgw seek action = ", action)

	  audioEngine.sendLoopCommand(null, null);
	  audioEngine.sendSeekCommand(action.payload);
	  state.isLooping = false;
	},


    stopSong: (state) => {
      try {
        if (audioEngine) {
          audioEngine.sendLoopCommand(null, null);
          state.isLooping = false;
          if (state.playbackState === "recording") {
            audioEngine.sendStopRecordCommand();
          } else {
            audioEngine.sendStopCommand();
          }
          
        }
      } catch (e) {
        console.log(e);
      }
      
      state.playbackState = "stopped";
    },
    addNewStem: (state, action) => {
      // unarm existing channels
      for (const userStem of state.userStems) {
        userStem.armed = false;
        audioEngine.sendChannelParamChange(userStem.index, "arm", false);
      }
      
      const newStem = {
        index: state.stems.length + state.userStems.length,
        id: `user-audio-${state.id}-${uuidv4().slice(0, 8)}`,
        label: action.payload.name,
        level: 0.8,
        pan: 0.5,
        reverb: {
          displayValue: "0",
          value: 0,
        },
        isInputChannel: true,
        muted: true,
        soloed: false,
        armed: true,
      };
      state.userStems = [...state.userStems, newStem];
      audioEngine.addInputChannel(
        action.payload.name,
        state.song.len,
        newStem.id
      );
      
    },
    toggleMasterMute: (state) => {
      state.masterMute = state.masterMute ? false : true;
      audioEngine.sendMasterMuteCommand(state.masterMute);
    },
    updateStatus: (state, action) => {
      state.status = action.payload;
    },
    setIntroLevel: (state, action) => {
      const foundChannel = state.stems.find((s) =>
      state.ui.clientConfig.features.session.introChannelNames.includes(s.id)
      );
      if (foundChannel) {
        foundChannel.level = action.payload;
        audioEngine.sendChannelParamChange(
          foundChannel.index,
          "channelGain",
          action.payload
        );
      }
    },
    setClick1Level: (state, action) => {
      const foundChannel = state.stems.find((s) =>
      state.ui.clientConfig.features.session.click1ChannelNames.includes(s.id)
      );
      if (foundChannel) {
        foundChannel.level = action.payload;
        audioEngine.sendChannelParamChange(
          foundChannel.index,
          "channelGain",
          action.payload
        );
      }
    },
    setClick2Level: (state, action) => {
      const foundChannel = state.stems.find((s) =>
      state.ui.clientConfig.features.session.click2ChannelNames.includes(s.id)
      );
      if (foundChannel) {
        foundChannel.level = action.payload;
        audioEngine.sendChannelParamChange(
          foundChannel.index,
          "channelGain",
          action.payload
        );
      }
    },
    setCueLevel: (state, action) => {
      const foundChannel = state.stems.find((s) =>
        state.ui.clientConfig.features.session.cueChannelNames.includes(s.id)
      );
      if (foundChannel) {
        foundChannel.level = action.payload;
        audioEngine.sendChannelParamChange(
          foundChannel.index,
          "channelGain",
          action.payload
        );
      }
    },
    setIsLooping: (state, action) => {
      

	if (doprint) console.log("pgw setIsLooping action = ", action)
	if (doprint) console.log("pgw setIsLooping state = ", state)

      if (action.payload.endMs) {
        state.loopStartMs = action.payload.startMs;
        state.loopEndMs = action.payload.endMs;
        state.isLooping = true;
      } else {
        state.loopStart = null;
        state.loopEnd = null;
        state.isLooping = false;
        
      }
		if (doprint) console.log("pgw setIsLooping state = ", state)
      audioEngine.sendLoopCommand(action.payload.startMs, action.payload.endMs);
    },
    setMediaSessionStarted: (state, action) => {
      state.mediaSessionStarted = action.payload;
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(mountSessionAsync.pending, (state) => {
        state.status = "loading";
      })


/*
      .addCase(mountSessionAsync.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message
        console.error(action);
      })
*/

	  .addCase(mountSessionAsync.rejected, (state, action) => {
		if (doprint) console.log("[SESSION] mount rejected", {
		  error: action.error,                 // { name, message, stack? }
		  payload: action.payload,             // if you used rejectWithValue
		  meta: {
			arg: action.meta?.arg,
			requestId: action.meta?.requestId,
			aborted: action.meta?.aborted,
			condition: action.meta?.condition,
			rejectedWithValue: action.meta?.rejectedWithValue,
		  },
		});

		// keep your existing logic:
        state.error = action.error.message
		state.status = "error";

		if (doprint) console.log("error !! mountSessionAsync.rejected action.error = ", action.error);
		if (doprint) console.log("error !! mountSessionAsync.rejected state.error = ", state.error);
		if (doprint) console.log("error !! mountSessionAsync.rejected action.payload = ", action.payload);

	  })


      .addCase(mountSessionAsync.fulfilled, (state, action) => {

		if (doprint) console.log("[MOUNT FULFILLED] state.origin before assign", state.origin);


		const prevOrigin = state.origin;

		if (doprint) console.log("[MOUNT FULFILLED] state.origin before Object.assign state = ", state);


  		Object.assign(state, action.payload);

		if (doprint) console.log("[MOUNT FULFILLED] state.origin after Object.assign state = ", state);


        Object.getOwnPropertyNames(action.payload).forEach((propertyName) => {
          state[propertyName] = action.payload[propertyName];
        });

		if (doprint) console.log("[MOUNT FULFILLED] state.origin after Object.getOwnPropertyNames state = ", state);

        state.status = "ready";


		if (doprint) console.log("[MOUNT FULFILLED] before check state.origin = ", state.origin);
		if (doprint) console.log("[MOUNT FULFILLED] before check prevOrigin = ", prevOrigin);

		// ✅ if engine payload didn’t include origin, keep previous
		if (!state.origin && prevOrigin) {
			if (doprint) console.log("[MOUNT FULFILLED] payload didn’t include origin - keep previous prevOrigin = ", prevOrigin);
			state.origin = prevOrigin;
		}

		if (doprint) console.log("[MOUNT FULFILLED] state.origin after assign", state.origin);

      })
      .addCase(mountSessionAndRenderOfflineAsync.pending, (state) => {
        state.status = "rendering";
      })
      .addCase(mountSessionAndRenderOfflineAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(mountSessionAndRenderOfflineAsync.fulfilled, (state, action) => {
        window.location.reload();
        state.status = "ready";
      })
      .addCase(unmountSessionAsync.pending, (state) => {
        Object.getOwnPropertyNames(initialState).forEach((propertyName) => {
          state[propertyName] = initialState[propertyName];
        });
      })
      .addCase(unmountSessionAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(unmountSessionAsync.fulfilled, (state, action) => {
        Object.getOwnPropertyNames(initialState).forEach((propertyName) => {
          state[propertyName] = action.payload[propertyName];
        });
      })
      .addCase(attachUserMicrophoneAsync.pending, (state) => {
        state.userAudio = "pending";
      })
      .addCase(attachUserMicrophoneAsync.fulfilled, (state, action) => {
        state.userAudio = "granted";
      })
      .addCase(packSessionAsync.pending, (state, action) => {
        state.status = "packing";
        state.loadingProgress = {step:6, downloadPercentage:100};
      })
      .addCase(packSessionAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(packSessionAsync.fulfilled, (state, action) => {
        state.status = "ready";
        state.loadingProgress = {step:10, downloadPercentage:100};
      })
      
      .addCase(resetSessionMixerSettings.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(resetSessionMixerSettings.fulfilled, (state, action) => {
        // let stemsToReset = state.stems.filter(c => !specialCaseChannels(state.ui.clientConfig).includes(c.id));
        let stemsToReset = state.stems;
        
        let userStems = state.userStems;
      
        for (let index = 0; index < stemsToReset.length; index++) {
          stemsToReset[index].level = 1;
          stemsToReset[index].pan = 0.5;
          stemsToReset[index].muted = false;
          stemsToReset[index].soloed = false;
        }
      
        for (let index = 0; index < userStems.length; index++) {
          userStems[index].level = 1;
          userStems[index].pan = 0.5;
          userStems[index].muted = false;
          userStems[index].soloed = false;
        }

        state.speed.value = 1;
        state.speed.displayValue = '100%';
        state.pitch.value = 0;
        state.pitch.displayValue = '0';
      })
      .addCase(removeChannelAsync.fulfilled, (state, action) => {
        state.userStems = [...state.userStems.filter(s => s.id !== action.payload.id)];

        for (const userStem of state.userStems) {
          if (userStem.index > action.payload.index) userStem.index = userStem.index - 1;
        }
      });
      ;

  },
});

export const {
  resetMixerState,
  changeChannelGain,
  changeGlobalPlaybackRate,
  decreaseGlobalPlaybackRate,
  increaseGlobalPlaybackRate,
  changeGlobalPitch,
  decreaseGlobalPitchRate,
  increaseGlobalPitchRate,
  muteChannel,
  unmuteChannel,
  soloChannel,
  unsoloChannel,
  changeChannelPan,
  updateProgress,
  resetLoadedStatus,
  playSong,
  pauseSong,
  stopSong,
  recordSong,
  addNewStem,
  armChannel,
  disarmChannel,
  toggleMasterMute,
  openSession,
  updateSessionMetadata,
  updateStatus,
  setIntroLevel,
  setClick1Level,
  setClick2Level,
  setCueLevel,
  setIsLooping,
  setMediaSessionStarted,
  seek
} = sessionSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectLoadedSong = (state) => state.session.loadedSong;
export const selectStatus = (state) => state.session.status;
export const selectIsLooping = (state) => state.session.isLooping;
export const selectLoopStart = (state) => state.session.loopStartMs;
export const selectLoopEnd = (state) => state.session.loopEndMs;
export const selectSession = (state) => state.session;
export const selectSections = (state) => state.session.sections;
export const selectTitle = (state) => state.session.title;
export const selectKey = (state) => state.session.key;
export const selectArtist = (state) => state.session.artist;
export const selectGenre = (state) => state.session.genre;
export const selectDecade = (state) => state.session.decade;
export const selectDuration = (state) => state.session.song?.len || 0;
export const selectUserStems = (state) => state.session.userStems;
export const selectSpeed = (state) => state.session.speed;
export const selectPitch = (state) => state.session.pitch;
export const selectCurrentPosition = (state) => state.session.currentPosition;
export const selectLoadingProgress = (state) => state.session.loadingProgress;
export const selectIsSafari = (state) => state.session.isSafari;
export const selectIsMasterMuted = (state) => state.session.masterMute;
export const selectPlaybackState = (state) => state.session.playbackState;
export const selectMediaSessionStarted = (state) => state.session.mediaSessionStarted;
export const selectError = (state) => state.session.error;

export const selectIntroLevel = (state) => {

  const foundChannel = state.session?.stems?.find((s) =>
    state.ui.clientConfig.features.session.introChannelNames.includes(s.id)
  );
  return foundChannel && state.session?.song ? foundChannel.level : null;
};

export const selectClick1Level = (state) => {
  const foundChannel = state.session?.stems?.find((s) =>
  state.ui.clientConfig.features.session.click1ChannelNames.includes(s.id)
  );
  return foundChannel && state.session?.song ? foundChannel.level : null;
};

export const selectClick2Level = (state) => {
  const foundChannel = state.session?.stems?.find((s) =>
  state.ui.clientConfig.features.session.click2ChannelNames.includes(s.id)
  );
  return foundChannel && state.session?.song ? foundChannel.level : null;
};

export const selectCueLevel = (state) => {
  const foundChannel = state.session?.stems?.find((s) =>
  state.ui.clientConfig.features.session.cueChannelNames.includes(s.id)
  );
  return foundChannel && state.session?.song ? foundChannel.level : null;
};

export const selectStems = (state) => {
  return state.session.stems;
};

export default sessionSlice.reducer;
